"use client";

import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import {
  aggregateMemberAttendanceStats,
  fetchStudioAttendanceLogs,
  type MemberAttendanceAggregate,
} from "@/lib/services/attendanceService";
import { QueryKeys } from "@/lib/types/query-keys";
import {
  MEMBER_ATTENDANCE_LOOKBACK_DAYS,
  memberAttendanceDateRange,
} from "@/lib/utils/attendance/memberAttendanceDashboard";

export function useStudioAttendanceStats(enabled: boolean): {
  data: MemberAttendanceAggregate | undefined;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
  isFetching: boolean;
} {
  const accessToken = useAccessToken();

  const q = useQuery({
    queryKey: [
      QueryKeys.Attendance,
      "studio-dashboard",
      MEMBER_ATTENDANCE_LOOKBACK_DAYS,
    ] as const,
    queryFn: async (): Promise<MemberAttendanceAggregate> => {
      if (!accessToken) {
        return {
          percentage: 0,
          attended: 0,
          total: 0,
          missed: 0,
        };
      }
      const { fromDate, toDate } = memberAttendanceDateRange();
      const logs = await fetchStudioAttendanceLogs(accessToken, fromDate, toDate);
      return aggregateMemberAttendanceStats(logs);
    },
    enabled: Boolean(accessToken && enabled),
    staleTime: 60_000,
    retry: false,
  });

  return {
    data: q.data,
    isPending: q.isPending,
    isError: q.isError,
    refetch: () => {
      void q.refetch();
    },
    isFetching: q.isFetching,
  };
}
